export type FilterType = 'entity_type' | 'property' | 'relationship_type' | 'document' | 'entity_id'

/** Property-level predicate used inside `ExclusionGroup`. The standalone
 * `useExploreFilters` hook that originally exported this type has been
 * deleted; the type stays here because `ExclusionGroup.filters` and
 * `useGraphExclusion`'s `addFilterToGroup` still use it. */
export interface ExploreFilter {
  id: string
  type: FilterType
  entityType?: string
  property?: string
  operator?:
    | '='
    | '!='
    | '>'
    | '<'
    | '>='
    | '<='
    | 'contains'
    | 'not_contains'
    | 'startsWith'
    | 'exists'
    | 'not_exists'
  value?: string
  relationshipType?: string
  documentId?: string
  entityId?: string
  entityName?: string
}

export interface ExclusionGroup {
  id: string
  filters: ExploreFilter[]
  operator: 'OR' | 'AND' // Operator AFTER this group (default OR)
}

export interface GraphExclusion {
  // Hidden entire entity types (OR logic - hide ALL entities of these types)
  hiddenEntityTypes: string[]

  // Hidden entities (always OR logic - hide if ANY match)
  hiddenEntityIds: string[]
  hiddenEntityNames: Record<string, string> // id -> name

  // Hidden relationship types (OR logic - hide if ANY match)
  hiddenRelationshipTypes: string[]

  // Hidden individual relationships (OR logic - hide if ANY match)
  hiddenRelationshipIds: string[]
  hiddenRelationshipNames: Record<string, string> // id -> label

  // Exclusion groups with operators (OR between groups by default)
  groups: ExclusionGroup[]
}

export const createEmptyGraphExclusion = (): GraphExclusion => ({
  hiddenEntityTypes: [],
  hiddenEntityIds: [],
  hiddenEntityNames: {},
  hiddenRelationshipTypes: [],
  hiddenRelationshipIds: [],
  hiddenRelationshipNames: {},
  groups: [],
})

export const createDefaultExclusionGroup = (): ExclusionGroup => ({
  id: `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  filters: [],
  operator: 'OR',
})

export const getExclusionSummaryText = (exclusion: GraphExclusion): string => {
  const parts: string[] = []

  if (exclusion.hiddenEntityTypes.length > 0) {
    parts.push(`${exclusion.hiddenEntityTypes.length} types hidden`)
  }

  if (exclusion.hiddenEntityIds.length > 0) {
    parts.push(`${exclusion.hiddenEntityIds.length} entities hidden`)
  }

  if (exclusion.hiddenRelationshipTypes.length > 0) {
    parts.push(`${exclusion.hiddenRelationshipTypes.length} relationship types hidden`)
  }

  if (exclusion.hiddenRelationshipIds.length > 0) {
    parts.push(`${exclusion.hiddenRelationshipIds.length} relationships hidden`)
  }

  if (exclusion.groups.length > 0) {
    parts.push(`${exclusion.groups.length} exclusion group(s)`)
  }

  return parts.length > 0 ? parts.join(' • ') : 'No exclusions (showing all)'
}
